import { IConfiguration, IOrderData } from './store-data';
import React from 'react';
import { getSearchParameter } from '../helpers/location-helper';
import { BASE_URL, getCoreAPIUrl } from '../helpers/urlHelper';
import { redirectToDecline } from '../helpers/parent-helper';
import { logger, loggerError } from '../helpers/logger';
import { IChallengeResponse } from '../helpers/fetch-service';
import * as Sentry from '@sentry/react';

export enum commonReducerActions {
  SET_CONFIGURATION = 'SET_TEMPLATE_ITEM',
  SET_CONFIGURATION_LOADING = 'SET_PAYMENT_TOKEN',
  SET_ORDER_DATA = 'SET_CARD_BRAND',
  SET_ORDER_DATA_LOADING = 'SET_CARD_LAST_DIGITS',
  SET_CHALLENGE_DATA = 'SET_CHALLENGE_DATA',
  SET_CHALLENGE_DATA_LOADING = 'SET_CHALLENGE_DATA_LOADING',
}

export interface IAction {
  type: string;
  payload: any;
}
interface IState {
  configuration: IConfiguration;
  configurationLoading: boolean;
  orderData: IOrderData | undefined;
  orderDataLoading: boolean;
  challengeTemplate: IChallengeResponse | null;
  challengeTemplateLoading: boolean;
}
type CommonProviderProps = { children: React.ReactNode };

const CommonStateContext = React.createContext<{ state: IState; dispatch: React.Dispatch<IAction> } | undefined>(
  undefined,
);

function commonReducer(state: IState, action: IAction): IState {
  switch (action.type) {
    case commonReducerActions.SET_CONFIGURATION: {
      return { ...state, configuration: action.payload, configurationLoading: false };
    }
    case commonReducerActions.SET_CONFIGURATION_LOADING: {
      return { ...state, configurationLoading: action.payload };
    }
    case commonReducerActions.SET_ORDER_DATA: {
      return { ...state, orderData: action.payload, orderDataLoading: false };
    }
    case commonReducerActions.SET_ORDER_DATA_LOADING: {
      return { ...state, orderDataLoading: action.payload };
    }
    case commonReducerActions.SET_CHALLENGE_DATA: {
      return { ...state, challengeTemplate: action.payload, challengeTemplateLoading: false };
    }
    case commonReducerActions.SET_CHALLENGE_DATA_LOADING: {
      return { ...state, challengeTemplateLoading: action.payload };
    }
    default: {
      return state;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function CommonProvider({ children }: CommonProviderProps) {
  const [state, dispatch] = React.useReducer(commonReducer, {
    configuration: {} as IConfiguration,
    orderData: undefined,
    configurationLoading: false,
    orderDataLoading: false,
    challengeTemplate: null,
    challengeTemplateLoading: false,
  });
  const value = { state, dispatch };

  return <CommonStateContext.Provider value={value}>{children}</CommonStateContext.Provider>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useCommon() {
  const context = React.useContext(CommonStateContext);
  if (context === undefined) {
    throw new Error('useCommon must be used within a CommonProvider');
  }
  return context;
}

export function fetchConfiguration(dispatch: React.Dispatch<IAction>): Promise<void> | undefined {
  const mid = getSearchParameter('mid');
  const siteId = getSearchParameter('siteId');

  if (mid) {
    dispatch({ type: commonReducerActions.SET_CONFIGURATION_LOADING, payload: true });

    const url = `${BASE_URL.CONFIGURATION}/${mid}/widget?siteId=${siteId}`;
    return fetch(url)
      .then(res => res.json())
      .then(res => {
        if (!res) {
          Sentry.captureMessage('WidgetShell: GET fetch configuration error');
          logger({ name: 'WidgetShell: GET fetch configuration error' });

          redirectToDecline();
        }
        dispatch({ type: commonReducerActions.SET_CONFIGURATION, payload: res });
      })
      .catch(error => {
        Sentry.captureMessage('WidgetShell: GET fetch configuration error', error.message);
        logger({ name: 'WidgetShell: GET fetch configuration error', message: error.message });

        loggerError(error);
        dispatch({ type: commonReducerActions.SET_CONFIGURATION_LOADING, payload: false });
        redirectToDecline();
      });
  }
}

export function fetchOrderSessionKey(
  dispatch: React.Dispatch<IAction>,
  requestUrl?: string,
): Promise<void> | undefined {
  const orderSessionKey = getSearchParameter('orderSessionKey');
  const mid = getSearchParameter('mid');
  const senseKey = getSearchParameter('senseKey');

  if (orderSessionKey && mid && senseKey) {
    dispatch({ type: commonReducerActions.SET_ORDER_DATA_LOADING, payload: true });

    const url = requestUrl || `${getCoreAPIUrl('eligibility')}/poll`;
    const body = { senseKey, mid, orderSessionKey };

    const options = {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      // credentials: 'include',
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return fetch(url, options)
      .then(res => res.json())
      .then(res => {
        if (!res) {
          logger({ name: 'GET order session response is empty', message: { error: 'response is empty' } });
          redirectToDecline();
        }
        dispatch({ type: commonReducerActions.SET_ORDER_DATA, payload: res as IOrderData });
      })
      .catch(error => {
        Sentry.captureMessage('WidgetShell: POST polling error', error.message);
        logger({ name: 'WidgetShell: POST polling error', message: error.message });

        loggerError(error);
        redirectToDecline();
        dispatch({ type: commonReducerActions.SET_ORDER_DATA_LOADING, payload: false });
      });
  }
}

export { CommonProvider, useCommon };
